import React, { useState, useRef } from 'react';
import { FileText, Download, X, Plus } from 'lucide-react';
import jsPDF from 'jspdf';
import './ToolsCommon.css';

const ImageToPDF = () => {
  const [images, setImages] = useState([]);
  const [pdfName, setPdfName] = useState('document');
  const [pageSize, setPageSize] = useState('a4');
  const [orientation, setOrientation] = useState('portrait');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          url: event.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const moveImage = (index, direction) => {
    const newImages = [...images];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < images.length) {
      [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
      setImages(newImages);
    }
  };

  const generatePDF = () => {
    if (images.length === 0) return;

    const pdf = new jsPDF({
      orientation: orientation,
      unit: 'mm',
      format: pageSize
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;

    images.forEach((image, index) => {
      if (index > 0) {
        pdf.addPage();
      }

      const img = new Image();
      img.src = image.url;

      const imgWidth = pageWidth - (margin * 2);
      const imgHeight = (img.height * imgWidth) / img.width;

      let finalHeight = imgHeight;
      let finalWidth = imgWidth;

      if (imgHeight > pageHeight - (margin * 2)) {
        finalHeight = pageHeight - (margin * 2);
        finalWidth = (img.width * finalHeight) / img.height;
      }

      const x = (pageWidth - finalWidth) / 2;
      const y = (pageHeight - finalHeight) / 2;

      pdf.addImage(image.url, 'JPEG', x, y, finalWidth, finalHeight);
    });

    pdf.save(`${pdfName}.pdf`);
  };

  return (
    <div className="tool-container">
      <div className="tool-content-wrapper">
        <div className="tool-header">
          <FileText size={48} className="tool-icon" />
          <h1 className="tool-title">Image to PDF Converter</h1>
          <p className="tool-subtitle">Convert multiple images into a single PDF document</p>
        </div>

        <div className="tool-panel">
          <div className="tool-section">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              multiple
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="tool-button primary"
              style={{ width: '100%' }}
            >
              <Plus size={20} />
              Add Images
            </button>
          </div>

          {images.length > 0 && (
            <>
              <div className="tool-grid">
                <div className="tool-section">
                  <label className="tool-label">PDF Name</label>
                  <input
                    type="text"
                    value={pdfName}
                    onChange={(e) => setPdfName(e.target.value)}
                    className="tool-input"
                    placeholder="document"
                  />
                </div>

                <div className="tool-section">
                  <label className="tool-label">Page Size</label>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(e.target.value)}
                    className="tool-input"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="a4">A4</option>
                    <option value="a3">A3</option>
                    <option value="a5">A5</option>
                    <option value="letter">Letter</option>
                    <option value="legal">Legal</option>
                  </select>
                </div>

                <div className="tool-section">
                  <label className="tool-label">Orientation</label>
                  <select
                    value={orientation}
                    onChange={(e) => setOrientation(e.target.value)}
                    className="tool-input"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </div>
              </div>

              <div className="tool-section">
                <label className="tool-label">Images ({images.length})</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '16px', marginTop: '16px' }}>
                  {images.map((image, index) => (
                    <div key={image.id} style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '2px solid #e5e7eb' }}>
                      <img src={image.url} alt={image.name} style={{ width: '100%', height: '150px', objectFit: 'cover' }} />
                      <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '4px' }}>
                        {index > 0 && (
                          <button
                            onClick={() => moveImage(index, 'up')}
                            style={{ background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '14px' }}
                          >
                            ↑
                          </button>
                        )}
                        {index < images.length - 1 && (
                          <button
                            onClick={() => moveImage(index, 'down')}
                            style={{ background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '14px' }}
                          >
                            ↓
                          </button>
                        )}
                        <button
                          onClick={() => removeImage(image.id)}
                          style={{ background: 'rgba(239,68,68,0.9)', color: 'white', border: 'none', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div style={{ padding: '8px', background: '#f9fafb', fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                        Page {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={generatePDF} className="tool-button primary" style={{ width: '100%', marginTop: '24px' }}>
                <Download size={20} />
                Generate PDF ({images.length} pages)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageToPDF;
